import React, { useState } from 'react';
import usePageTranslation from '../../../../shared/hooks/usePageTranslation';
import { BUILDING_INTERVENTION_SPAWN_RADIUS_KM } from '../../../../shared/constants/gameMap';
import type { InstanceRow, SectorVehicleRow } from './instanceModel';
import {
  categoryGlyph,
  DispatcherHeadsetIcon,
  effectiveMoyensRequired,
  formatDurationMmSs,
  formatSectorVehicleLines,
  getInterventionDisplayLines,
  getInterventionPhaseAccent,
  getInterventionPhaseUiKey,
  interventionPhaseI18nKey,
  InterventionPhaseTrack,
  isScenarioPartageEligible,
  normalizeInterventionInstanceStatut,
} from './instanceModel';

export type InterventionManageModalProps = {
  manageId: number | null;
  onClose: () => void;
  instances: InstanceRow[];
  availableVehicles: Array<{ id: number; nom?: string; id_status?: number; vehicule_type_id?: number }>;
  selectedVehIdsByInstance: Record<number, number[]>;
  toggleSectorVehicle: (instanceId: number, vehId: number) => void;
  selectAllSectorVehicles: (instanceId: number, list: SectorVehicleRow[]) => void;
  clearSectorSelection: (instanceId: number) => void;
  sectorVehicles: SectorVehicleRow[];
  sectorAlreadyEngaged: Array<{
    id: number;
    nom?: string | null;
    type_nom?: string | null;
    statut?: string | null;
    eta_remaining_sec?: number | null;
  }>;
  sectorLoading: boolean;
  sectorError: string | null;
  busyId: number | null;
  assignVehicle: (instanceId: number) => void | Promise<void>;
  advance: (instanceId: number, outcome?: 'success' | 'failure') => void | Promise<void>;
  unassignReservedVehicle: (instanceId: number, vehiculeId: number) => void | Promise<void>;
  recallInterventionVehicles: (instanceId: number) => void | Promise<void>;
  recallInterventionVehicle: (instanceId: number, vehiculeId: number) => void | Promise<void>;
  reengageInterventionVehicle: (instanceId: number, vehiculeId: number) => void | Promise<void>;
  handleDeploy: (inv: InstanceRow) => void;
  manageCompletion: {
    canFinishSuccess: boolean;
    lines: string[];
    tempsMin: number;
    req: number;
    total: number;
    ph: string | null;
    remainingOnSiteSec: number | null;
  };
  titleLocale: string;
};

function etaMin(distanceKm: number | null | undefined): number | null {
  if (distanceKm == null || !Number.isFinite(Number(distanceKm))) return null;
  return Math.max(1, Math.ceil((Number(distanceKm) / 45) * 60));
}

function distanceLabel(distanceKm: number | null | undefined): string {
  if (distanceKm == null || !Number.isFinite(Number(distanceKm))) return '—';
  return `${Number(distanceKm).toFixed(1)} km`;
}

function pct(value: number, total: number): number {
  if (total <= 0) return 100;
  return Math.max(0, Math.min(100, Math.round((value / total) * 100)));
}

function dispatchStatusLabel(raw: string | null | undefined): string {
  const s = String(raw || '').toLowerCase();
  if (s === 'en_alerte' || s === 'prealerte') return 'En alerte';
  if (s === 'en_route') return 'En route';
  if (s === 'sur_les_lieux' || s === 'sur_place') return 'Sur les lieux';
  if (s === 'transport_hopital') return 'Transport hôpital';
  if (s === 'arrive_hopital') return 'Arrivée hôpital';
  if (s === 'retour_disponible_radio' || s === 'disponible_radio') return 'Retour disponible radio';
  if (s === 'retour_disponible_hors_secteur' || s === 'disponible_hors_secteur' || s === 'retour_hors_secteur')
    return 'Retour disponible hors secteur';
  if (s === 'retour_non_operationnel') return 'Retour non opérationnel';
  return s || 'Inconnu';
}

function formatEtaMmSs(secRaw: number | null | undefined): string | null {
  const sec = Math.max(0, Math.floor(Number(secRaw ?? NaN)));
  if (!Number.isFinite(sec) || sec < 1) return null;
  const mm = Math.floor(sec / 60);
  const ss = sec % 60;
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

const InterventionManageModal: React.FC<InterventionManageModalProps> = ({
  manageId,
  onClose,
  instances,
  availableVehicles,
  selectedVehIdsByInstance,
  toggleSectorVehicle,
  selectAllSectorVehicles,
  clearSectorSelection,
  sectorVehicles,
  sectorAlreadyEngaged,
  sectorLoading,
  sectorError,
  busyId,
  assignVehicle,
  advance,
  unassignReservedVehicle,
  recallInterventionVehicles,
  recallInterventionVehicle,
  reengageInterventionVehicle,
  handleDeploy,
  manageCompletion,
  titleLocale,
}) => {
  const { t } = usePageTranslation('game', 'Map');
  const [search, setSearch] = useState('');
  const [sortMode, setSortMode] = useState<'distance_asc' | 'distance_desc' | 'building' | 'vehicle'>('distance_asc');
  const [onlySelected, setOnlySelected] = useState(false);
  const [onlyInRange, setOnlyInRange] = useState(false);

  if (manageId == null) return null;
  const inv = instances.find((i) => i.id === manageId);
  if (!inv) return null;

  const statusApi = normalizeInterventionInstanceStatut(inv.statut || '');
  const phase = inv.gameplay_phase;
  const phaseUiKey = getInterventionPhaseUiKey(inv);
  const phaseAccent = getInterventionPhaseAccent(phaseUiKey);
  const phaseLabel = t(interventionPhaseI18nKey(phaseUiKey));
  const isWaiting = phase === 'WAITING' || statusApi === 'EN_ATTENTE';
  const isPreAlert = phase === 'ASSIGNED';
  const isTraveling = phase === 'TRAVELING';
  const isOnSite = phase === 'ON_SITE';
  const isHospitalTransport = phase === 'HOSPITAL_TRANSPORT';
  const isHospitalArrival = phase === 'HOSPITAL_ARRIVAL';
  const isReturning = phase === 'RETURNING';
  const busy = busyId === inv.id;

  const { headline, subline, glyphHint } = getInterventionDisplayLines(inv, t('interventionSidebar.missionFallback'));
  const titleUpper = headline.toLocaleUpperCase(titleLocale);
  const missionRef = inv.numero_intervention
    ? String(t('interventionSidebar.refShort')).replace(/\{\{num\}\}/g, inv.numero_intervention || '')
    : `#${inv.id}`;
  const glyph = categoryGlyph(glyphHint);

  const required = effectiveMoyensRequired(inv);
  const dispatched = Number(inv.dispatch_total || 0);
  const selectedIds = selectedVehIdsByInstance[inv.id] || [];
  const selectedCount = selectedIds.length;
  const projected = dispatched + selectedCount;
  const remaining = Math.max(0, required - projected);

  const selectedRows = sectorVehicles.filter((v) => selectedIds.includes(v.id));
  const etaValues = selectedRows.map((v) => etaMin(v.distance_km)).filter((x): x is number => x != null);
  const etaAvg = etaValues.length > 0 ? Math.round(etaValues.reduce((a, b) => a + b, 0) / etaValues.length) : null;
  const etaMinVal = etaValues.length > 0 ? Math.min(...etaValues) : null;
  const etaMaxVal = etaValues.length > 0 ? Math.max(...etaValues) : null;

  const byBuilding: Record<string, number> = {};
  const byType: Record<string, number> = {};
  selectedRows.forEach((v) => {
    const building = String(v.batiment_nom || 'Bâtiment inconnu');
    const type = String(v.type_nom || 'Type non renseigné');
    byBuilding[building] = (byBuilding[building] || 0) + 1;
    byType[type] = (byType[type] || 0) + 1;
  });

  const rows = (() => {
    const needle = search.trim().toLowerCase();
    const filtered = sectorVehicles.filter((v) => {
      const { title, subtitle } = formatSectorVehicleLines(v);
      const matchSearch = !needle
        || title.toLowerCase().includes(needle)
        || subtitle.toLowerCase().includes(needle)
        || String(v.type_nom || '').toLowerCase().includes(needle)
        || String(v.batiment_nom || '').toLowerCase().includes(needle);
      if (!matchSearch) return false;
      if (onlySelected && !selectedIds.includes(v.id)) return false;
      if (onlyInRange && Number(v.distance_km || 0) > BUILDING_INTERVENTION_SPAWN_RADIUS_KM) return false;
      return true;
    });
    filtered.sort((a, b) => {
      if (sortMode === 'distance_asc') return Number(a.distance_km ?? 9999) - Number(b.distance_km ?? 9999);
      if (sortMode === 'distance_desc') return Number(b.distance_km ?? -1) - Number(a.distance_km ?? -1);
      if (sortMode === 'building') return String(a.batiment_nom || '').localeCompare(String(b.batiment_nom || ''), titleLocale);
      return String(a.nom || '').localeCompare(String(b.nom || ''), titleLocale);
    });
    return filtered;
  })();

  const inRangeCount = sectorVehicles.filter((v) => Number(v.distance_km || 0) <= BUILDING_INTERVENTION_SPAWN_RADIUS_KM).length;
  const canEngage = isWaiting || isPreAlert;
  const canDeploy =
    isWaiting || isPreAlert || isTraveling || isHospitalTransport || isHospitalArrival || isReturning;
  const nextStepLabel = isWaiting || isPreAlert
    ? 'Engager (alerte → départ)'
    : isTraveling
      ? 'Signaler arrivée sur les lieux'
      : isHospitalTransport
        ? 'Signaler arrivée à l’hôpital'
        : isHospitalArrival
          ? 'Lancer le retour vers le centre'
          : isReturning
            ? 'Terminer le retour et rendre disponible'
            : t('interventionSidebar.deploy');
  const coverageBefore = pct(dispatched, required);
  const coverageAfter = pct(projected, required);

  const checkpoints = [
    { label: 'Objectif de moyens', ok: projected >= required, detail: `${projected}/${required}` },
    { label: 'Sélection prête', ok: selectedCount > 0, detail: `${selectedCount} véhicule(s)` },
    { label: 'Fenêtre d’engagement', ok: canEngage, detail: canEngage ? 'Ouverte' : 'Fermée' },
    {
      label: 'Délai opérationnel',
      ok: manageCompletion.remainingOnSiteSec == null || manageCompletion.remainingOnSiteSec > 0,
      detail: manageCompletion.remainingOnSiteSec != null ? formatDurationMmSs(manageCompletion.remainingOnSiteSec) : 'Aucun compte à rebours',
    },
  ];

  return (
    <div className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/85 p-1 sm:p-3 lg:p-4" role="presentation" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="intervention-manage-title"
        aria-describedby="intervention-manage-subtitle"
        className="flex h-[99dvh] w-full max-w-[1960px] flex-col overflow-hidden rounded-none border border-slate-700/90 bg-slate-950 shadow-2xl shadow-black/60 sm:h-[96vh] sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className={`relative shrink-0 border-t-[3px] ${phaseAccent.topBorder} bg-gradient-to-b from-slate-800/95 via-slate-900/95 to-slate-950 px-3 pb-3 pt-3 sm:px-5 sm:pb-4 sm:pt-4`}>
          <button type="button" className="absolute right-3 top-3 rounded-md p-1.5 text-slate-400 hover:bg-white/10 hover:text-white sm:right-4 sm:top-4" aria-label={t('interventionSidebar.closeModal')} onClick={onClose}>
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div className="grid gap-3 lg:grid-cols-[minmax(0,1.7fr),minmax(0,1fr)]">
            <div className="flex gap-3 pr-10">
              <DispatcherHeadsetIcon className={`h-12 w-12 shrink-0 ${phaseAccent.headset}`} />
              <div className="min-w-0">
                <p className={`mb-1 text-[10px] font-semibold uppercase tracking-[0.2em] ${phaseAccent.kicker}`}>Cellule de commandement intervention</p>
                <h2 id="intervention-manage-title" className="break-words text-base font-extrabold uppercase tracking-wide text-white sm:text-lg lg:text-xl">{titleUpper}</h2>
                <p id="intervention-manage-subtitle" className="mt-1 text-xs text-slate-200 sm:text-sm">{subline || 'Adresse non renseignée'}</p>
                <p className="mt-1 font-mono text-[10px] text-slate-400">{missionRef} · {phaseLabel} · Partage alliance : {isScenarioPartageEligible(inv) ? 'éligible' : 'non éligible'}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 text-[11px] sm:grid-cols-4">
              <div className="rounded border border-slate-700/75 bg-slate-900/70 p-2"><p className="text-[10px] uppercase text-slate-500">Requis</p><p className="mt-1 text-lg font-semibold text-red-200">{required}</p></div>
              <div className="rounded border border-slate-700/75 bg-slate-900/70 p-2"><p className="text-[10px] uppercase text-slate-500">Engagés</p><p className="mt-1 text-lg font-semibold text-slate-100">{dispatched}</p></div>
              <div className="rounded border border-emerald-500/35 bg-emerald-950/20 p-2"><p className="text-[10px] uppercase text-emerald-300">Sélection</p><p className="mt-1 text-lg font-semibold text-emerald-100">{selectedCount}</p></div>
              <div className="rounded border border-amber-500/35 bg-amber-950/20 p-2"><p className="text-[10px] uppercase text-amber-300">Reste</p><p className="mt-1 text-lg font-semibold text-amber-100">{remaining}</p></div>
            </div>
          </div>

          <div className="mt-3 grid gap-2 lg:grid-cols-[minmax(0,1.7fr),minmax(0,1fr)]">
            <div className="flex items-center gap-2.5 rounded-lg bg-slate-900/80 px-2 py-2 ring-1 ring-slate-800/90">
              <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-md border-2 text-xl leading-none shadow-inner ${phaseAccent.glyphBox}`}>{glyph}</div>
              <InterventionPhaseTrack phase={phaseUiKey} />
            </div>
            <div className="rounded border border-slate-700/70 bg-slate-900/70 px-3 py-2">
              <p className="text-[10px] uppercase text-slate-500">Couverture opérationnelle</p>
              <div className="mt-2 space-y-2">
                <div>
                  <div className="mb-1 flex justify-between text-[10px] text-slate-400"><span>Avant</span><span>{coverageBefore}%</span></div>
                  <div className="h-1.5 rounded bg-slate-800"><div className="h-1.5 rounded bg-slate-500" style={{ width: `${coverageBefore}%` }} /></div>
                </div>
                <div>
                  <div className="mb-1 flex justify-between text-[10px] text-slate-400"><span>Après</span><span>{coverageAfter}%</span></div>
                  <div className="h-1.5 rounded bg-slate-800"><div className={`h-1.5 rounded ${projected >= required ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{ width: `${coverageAfter}%` }} /></div>
                </div>
              </div>
            </div>
          </div>
        </header>

        <main className="grid min-h-0 flex-1 gap-2 overflow-hidden px-2 py-2 sm:gap-3 sm:px-3 sm:py-3 xl:grid-cols-[minmax(0,1.3fr),minmax(0,2fr),minmax(0,1.1fr)]">
          <section className="flex min-h-0 flex-col rounded-xl border border-slate-700/70 bg-slate-900/40 p-2 sm:p-3">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-200">Situation · Chef de salle</h3>
            <div className="rounded border border-slate-700/70 bg-slate-950/70 p-2 text-[11px]">
              <p className="text-[10px] uppercase text-slate-500">Synthèse immédiate</p>
              <p className="mt-1 text-slate-200">Moyens projetés : {projected}/{required}</p>
              <p className="text-slate-400">Flotte disponible : {availableVehicles.length} · Secteur : {sectorVehicles.length}</p>
              <p className="text-slate-400">Dans le rayon : {inRangeCount} · Hors rayon : {Math.max(0, sectorVehicles.length - inRangeCount)}</p>
              <p className="text-slate-400">ETA moyenne sélection : {etaAvg != null ? `${etaAvg} min` : '—'}</p>
              <p className="text-slate-400">Fenêtre ETA : {etaMinVal != null && etaMaxVal != null ? `${etaMinVal}-${etaMaxVal} min` : 'Aucune'}</p>
            </div>

            <div className="mt-2 min-h-0 flex-1 overflow-auto rounded border border-slate-700/70 bg-slate-950/70 p-2 text-[11px]">
              <p className="mb-1 text-[10px] uppercase text-slate-500">Checklist décisionnelle</p>
              <div className="space-y-1.5">
                {checkpoints.map((item) => (
                  <div key={item.label} className={`rounded border px-2 py-1.5 ${item.ok ? 'border-emerald-600/40 bg-emerald-950/20' : 'border-amber-600/40 bg-amber-950/20'}`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className={`text-[11px] font-semibold ${item.ok ? 'text-emerald-200' : 'text-amber-200'}`}>{item.label}</p>
                      <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${item.ok ? 'bg-emerald-700/30 text-emerald-100' : 'bg-amber-700/30 text-amber-100'}`}>{item.ok ? 'OK' : 'Alerte'}</span>
                    </div>
                    <p className="text-[10px] text-slate-400">{item.detail}</p>
                  </div>
                ))}
              </div>
            </div>

            {manageCompletion.lines.length > 0 ? (
              <div className="mt-2 rounded border border-amber-500/40 bg-amber-950/20 p-2 text-[11px] text-amber-100">
                {manageCompletion.lines.map((line) => <p key={line}>{line}</p>)}
              </div>
            ) : null}
          </section>

          <section className="flex min-h-0 flex-col rounded-xl border border-slate-700/70 bg-slate-900/40 p-2 sm:p-3">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-200">Préparation · Mur des moyens</h3>
              <span className="rounded border border-slate-600/80 bg-slate-950/80 px-2 py-0.5 text-[10px] text-slate-300">
                {rows.length}/{sectorVehicles.length} visibles · rayon {BUILDING_INTERVENTION_SPAWN_RADIUS_KM} km
              </span>
            </div>

            <div className="mb-2 grid grid-cols-1 gap-2 md:grid-cols-[minmax(0,1fr),220px]">
              <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filtrer par véhicule, type, bâtiment..." className="rounded border border-slate-700/70 bg-slate-950/90 px-2 py-1.5 text-[11px] text-slate-100 placeholder:text-slate-500 outline-none focus:border-emerald-500/60" />
              <select value={sortMode} onChange={(e) => setSortMode(e.target.value as 'distance_asc' | 'distance_desc' | 'building' | 'vehicle')} className="rounded border border-slate-700/70 bg-slate-950/90 px-2 py-1.5 text-[11px] text-slate-100 outline-none focus:border-emerald-500/60">
                <option value="distance_asc">Distance : proche vers loin</option>
                <option value="distance_desc">Distance : loin vers proche</option>
                <option value="building">Tri : bâtiment</option>
                <option value="vehicle">Tri : véhicule</option>
              </select>
            </div>

            <div className="mb-2 flex flex-wrap items-center justify-between gap-2 border-b border-slate-800/90 pb-2">
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => setOnlySelected((v) => !v)} className={`rounded-md border px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide ${onlySelected ? 'border-violet-500/60 bg-violet-900/35 text-violet-100' : 'border-slate-600 bg-slate-800/80 text-slate-300'}`}>
                  {onlySelected ? 'Vue sélection' : 'Uniquement sélection'}
                </button>
                <button type="button" onClick={() => setOnlyInRange((v) => !v)} className={`rounded-md border px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide ${onlyInRange ? 'border-cyan-500/60 bg-cyan-900/30 text-cyan-100' : 'border-slate-600 bg-slate-800/80 text-slate-300'}`}>
                  {onlyInRange ? 'Vue dans rayon' : 'Filtrer hors rayon'}
                </button>
                <button type="button" disabled={busy || rows.length === 0} onClick={() => selectAllSectorVehicles(inv.id, rows)} className="rounded-md border border-sky-600/50 bg-sky-950/40 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-sky-200 hover:bg-sky-900/50 disabled:opacity-40">Tout sélectionner</button>
                <button type="button" disabled={busy || selectedCount === 0} onClick={() => clearSectorSelection(inv.id)} className="rounded-md border border-slate-600 bg-slate-800/80 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-300 hover:bg-slate-700 disabled:opacity-40">Réinitialiser</button>
              </div>
              <span className="rounded-md bg-slate-800/90 px-2 py-1 text-[10px] text-slate-300">{selectedCount} sélectionné(s)</span>
            </div>

            {sectorError ? <p className="mb-2 rounded border border-red-500/40 bg-red-950/30 px-2 py-1.5 text-xs text-red-300">{sectorError}</p> : null}
            {sectorLoading ? <p className="mb-2 text-xs text-slate-500">{t('loading.connect')}</p> : null}

            <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-slate-700/80 bg-black/30">
              <div className="sticky top-0 z-10 grid min-w-[760px] grid-cols-[34px,minmax(0,1fr),120px,minmax(0,0.8fr),90px,80px] border-b border-slate-700/70 bg-slate-900/95 px-3 py-1 text-[10px] uppercase tracking-wide text-slate-400">
                <span /><span>Véhicule</span><span>Type</span><span>Bâtiment</span><span className="text-right">Distance</span><span className="text-right">ETA</span>
              </div>
              {rows.map((v) => {
                const selected = selectedIds.includes(v.id);
                const { title, subtitle } = formatSectorVehicleLines(v);
                const eta = etaMin(v.distance_km);
                return (
                  <label key={v.id} className={`grid min-w-[760px] cursor-pointer grid-cols-[34px,minmax(0,1fr),120px,minmax(0,0.8fr),90px,80px] items-center gap-2 border-b border-slate-800/80 px-3 py-2 text-[11px] last:border-b-0 hover:bg-white/[0.04] ${selected ? 'bg-emerald-950/20' : ''}`}>
                    <input type="checkbox" checked={selected} disabled={busy} onChange={() => toggleSectorVehicle(inv.id, v.id)} className="h-4 w-4 rounded border-slate-500 text-emerald-500 focus:ring-emerald-500/40" />
                    <span className="min-w-0"><span className="block truncate font-medium text-slate-100">{title}</span>{subtitle ? <span className="mt-0.5 block truncate text-[10px] text-slate-400">{subtitle}</span> : null}</span>
                    <span className="truncate text-[10px] text-cyan-200">{String(v.type_nom || 'N/A')}</span>
                    <span className="truncate text-slate-300">{String(v.batiment_nom || '—')}</span>
                    <span className="text-right text-slate-300">{distanceLabel(v.distance_km)}</span>
                    <span className="text-right text-sky-300">{eta != null ? `${eta} min` : '—'}</span>
                  </label>
                );
              })}
            </div>
          </section>

          <section className="flex min-h-0 flex-col rounded-xl border border-slate-700/70 bg-slate-900/40 p-2 sm:p-3">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-200">Décision · Conduite de mission</h3>

            <div className="mb-2 rounded border border-slate-700/70 bg-slate-950/70 p-2 text-[11px]">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-300">
                Moyens engagés CTA ({sectorAlreadyEngaged.length})
              </p>
              <p className="mt-0.5 text-[10px] text-slate-500">
                Gestion directe des véhicules déjà liés à cette intervention.
              </p>
              <div className="mt-2 max-h-40 space-y-1 overflow-auto">
                {sectorAlreadyEngaged.length < 1 ? (
                  <p className="rounded border border-slate-700/70 bg-slate-900/70 px-2 py-1 text-[10px] text-slate-400">
                    Aucun véhicule encore engagé.
                  </p>
                ) : (
                  sectorAlreadyEngaged.map((v) => {
                    const st = String(v.statut || '').toLowerCase();
                    const canUnassign = st === 'en_alerte' || st === 'prealerte';
                    const canRecallOne = ['en_route', 'sur_les_lieux', 'sur_place', 'transport_hopital', 'arrive_hopital'].includes(st);
                    const canReengage = ['retour_disponible_radio', 'retour_disponible_hors_secteur', 'retour_non_operationnel', 'retour', 'disponible_radio', 'disponible_hors_secteur'].includes(st);
                    return (
                      <div
                        key={`engaged-cta-${v.id}`}
                        className="flex items-center justify-between gap-2 rounded border border-slate-800/80 bg-slate-900/60 px-2 py-1"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-[11px] text-slate-200">
                            {(v.nom || `#${v.id}`) + (v.type_nom ? ` · ${v.type_nom}` : '')}
                          </p>
                          <p className="text-[10px] text-slate-400">{dispatchStatusLabel(st)}</p>
                          {formatEtaMmSs(v.eta_remaining_sec) ? (
                            <p className="text-[10px] text-sky-300">
                              ETA restant: {formatEtaMmSs(v.eta_remaining_sec)}
                            </p>
                          ) : null}
                        </div>
                        {canUnassign ? (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => unassignReservedVehicle(inv.id, v.id)}
                            className="rounded border border-amber-500/40 bg-amber-950/30 px-2 py-0.5 text-[10px] font-semibold text-amber-200 hover:bg-amber-900/40 disabled:opacity-40"
                          >
                            Retirer
                          </button>
                        ) : canRecallOne ? (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => recallInterventionVehicle(inv.id, v.id)}
                            className="rounded border border-orange-500/40 bg-orange-950/30 px-2 py-0.5 text-[10px] font-semibold text-orange-200 hover:bg-orange-900/40 disabled:opacity-40"
                          >
                            Rappeler
                          </button>
                        ) : canReengage ? (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => reengageInterventionVehicle(inv.id, v.id)}
                            className="rounded border border-sky-500/40 bg-sky-950/30 px-2 py-0.5 text-[10px] font-semibold text-sky-200 hover:bg-sky-900/40 disabled:opacity-40"
                          >
                            Réengager
                          </button>
                        ) : (
                          <span className="rounded border border-slate-600/60 bg-slate-800/70 px-2 py-0.5 text-[10px] text-slate-300">
                            Suivi auto
                          </span>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <div className="rounded border border-slate-700/70 bg-slate-950/70 p-2 text-[11px]">
              <p className="text-[10px] uppercase text-slate-500">Répartition des moyens sélectionnés</p>
              <p className="mt-1 text-slate-200">ETA moyenne : {etaAvg != null ? `${etaAvg} min` : '—'}</p>
              <p className="text-slate-400">Fenêtre ETA : {etaMinVal != null && etaMaxVal != null ? `${etaMinVal}-${etaMaxVal} min` : 'Aucune'}</p>
              <div className="mt-2 space-y-1 text-[11px]">
                {Object.entries(byBuilding).length > 0
                  ? Object.entries(byBuilding).map(([name, count]) => <p key={name} className="flex justify-between"><span className="truncate pr-2 text-slate-300">{name}</span><span className="font-semibold text-emerald-300">{count}</span></p>)
                  : <p className="text-slate-500">Aucun bâtiment couvert.</p>}
              </div>
            </div>

            {isOnSite && manageCompletion.remainingOnSiteSec != null && manageCompletion.remainingOnSiteSec > 0 ? (
              <div className="mt-2 rounded border border-amber-500/45 bg-amber-950/25 px-3 py-2 text-center">
                <p className="text-[10px] uppercase tracking-[0.16em] text-amber-200">{t('interventionSidebar.onSiteCountdownLabel')}</p>
                <p className="mt-1 font-mono text-2xl font-semibold text-amber-50">{formatDurationMmSs(manageCompletion.remainingOnSiteSec)}</p>
              </div>
            ) : null}

            <div className="mt-auto grid gap-2 pt-2">
              <button type="button" onClick={onClose} className="rounded border border-slate-600/70 bg-slate-800/80 px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-slate-200 hover:bg-slate-700">Fermer</button>
              {canEngage ? (
                <button type="button" disabled={busy || selectedCount < 1} onClick={() => assignVehicle(inv.id)} className="rounded border border-emerald-400/30 bg-emerald-600 px-3 py-2 text-[10px] font-bold uppercase tracking-wide text-white hover:bg-emerald-500 disabled:opacity-50">
                  {busy ? '...' : '1 · Réserver (préalerte)'}
                </button>
              ) : null}
              {canDeploy ? (
                <button type="button" disabled={busy || (isWaiting && selectedCount === 0)} onClick={() => handleDeploy(inv)} className="rounded border border-emerald-500/50 bg-emerald-700 px-3 py-2 text-[10px] font-bold uppercase tracking-wide text-white hover:bg-emerald-600 disabled:opacity-50">
                  2 · {nextStepLabel}
                </button>
              ) : null}
              {(isTraveling || isOnSite || isHospitalTransport || isHospitalArrival) ? (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => recallInterventionVehicles(inv.id)}
                  className="rounded border border-orange-500/40 bg-orange-700/90 px-3 py-2 text-[10px] font-bold uppercase tracking-wide text-white hover:bg-orange-600 disabled:opacity-50"
                >
                  Rappeler les moyens (retour)
                </button>
              ) : null}
              {isOnSite ? (
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" disabled={busy || !manageCompletion.canFinishSuccess} onClick={() => advance(inv.id, 'success')} className="rounded border border-green-400/25 bg-green-600 px-3 py-2 text-[10px] font-bold uppercase tracking-wide text-white hover:bg-green-500 disabled:opacity-50">
                    {t('interventionSidebar.finishOk')}
                  </button>
                  <button type="button" disabled={busy} onClick={() => advance(inv.id, 'failure')} className="rounded border border-red-400/25 bg-red-600 px-3 py-2 text-[10px] font-bold uppercase tracking-wide text-white hover:bg-red-500 disabled:opacity-50">
                    {t('interventionSidebar.finishKo')}
                  </button>
                </div>
              ) : null}
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};

export default InterventionManageModal;
